/**
 * Partner/Admin "Tasks" domain (prompts.*), plus the handful of adjacent
 * namespaces the UiPrompts screens pull from - built on apiClient via
 * useRequest, not useAppServices(). Categories/subcategories CRUD for the
 * *custom*-prompt side lives in queries/promptCategories.js instead (sizable
 * enough, and conceptually distinct, to earn its own file); everything here
 * is either the partner/admin task list itself or a one-off lookup several
 * UiPrompts screens happen to share:
 *   - adminprompts/AdminPrompt.js, adminprompts/Prompts.js,
 *     customPrompts/CustomPrompt.js, customPrompts/ModelPrompts/Create.js ->
 *     partner-task hooks (usePartnerTasks/useCreatePartnerTask/
 *     useUpdatePartnerTask/useDeletePartnerTask/useGeneratePartnerTaskPrompt)
 *   - adminprompts/AdminPrompt.js -> useAdminPromptCategories
 *   - adminprompts/AdminPrompt.js, customPrompts/ModelPrompts/{Create,Edit}.js,
 *     customPrompts/Prompts.js -> useAvailableBranding (same transform
 *     inlined at all 4 call sites previously)
 *   - customPrompts/ModelPrompts/Create.js -> useCustomTaskAssistant
 *   - promptlabels/CustomPrompt.js -> useSuperAdminSettings
 *   - promptlabels/ModelPrompts/Create.js -> useModelOptions, useCreateAssistant
 *   - promptlabels/ModelPrompts/{Create,Edit}.js -> useCreateTemplate/useUpdateTemplate
 *
 * partnerTaskKeys.list always keys as (type, agencyId) - the two existing
 * call sites used mismatched key shapes (['partner-tasks','SuperAdmin'] vs
 * ['partner-tasks','agency',id]); standardized here since nothing outside
 * this migration reads those exact arrays and both still match the shared
 * ['partner-tasks'] prefix every invalidate already uses.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const partnerTaskKeys = {
  all: ["partner-tasks"],
  list: (type, agencyId) => ["partner-tasks", type, agencyId],
};

export function usePartnerTasks({ agencyId, type }, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: partnerTaskKeys.list(type, agencyId),
    queryFn: async () => {
      const { response } = await request(GET, "/prompts/partner", {
        query: `agency_id=${agencyId}&type=${type}`,
      });
      return response?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreatePartnerTask() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/prompts/partner", { payload }),
  });
}

// Shared by the toggle-active switch and the full edit-modal save - both PUT
// the same /prompts/partner endpoint, just with a different payload shape.
export function useUpdatePartnerTask() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/prompts/partner", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerTaskKeys.all }),
  });
}

export function useDeletePartnerTask() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => request(DELETE, "/prompts/delete", { query: `_id=${id}`, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: partnerTaskKeys.all }),
  });
}

// ModelPrompts/Create.js's "Generate task from a one-line description" step.
export function useGeneratePartnerTaskPrompt() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/prompts/agency/generate", { payload }),
  });
}

export function useAdminPromptCategories(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["prompt-categories", "super-admin"],
    queryFn: async () => {
      const { response } = await request(GET, "/prompts/category/filter", {
        query: "type=super-admin",
      });
      return (response?.data?.prompt_category || []).map((category) => ({
        value: category._id,
        label: category.name,
      }));
    },
    ...options,
  });
}

// Identical transform was duplicated at 4 call sites in the old AppService
// code (AdminPrompt.js, ModelPrompts/{Create,Edit}.js, customPrompts/Prompts.js).
export function useAvailableBranding(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["integrations"],
    queryFn: async () => {
      try {
        const { response } = await request(GET, "/integrations/available-branding");
        const responseData = response?.data || response || [];
        return responseData.map((acc) => ({
          value: acc.provider,
          label: acc.display_name || acc.provider,
        }));
      } catch (error) {
        console.error("Error fetching integrations:", error);
        return [];
      }
    },
    ...options,
  });
}

// On-demand lookup (not a reactive query) feeding ModelPrompts/Create.js's
// "Generate task" flow - mirrors useAgentBuilderData()'s GET-via-useMutation
// shape in queries/agency.js for the same reason: it's fired imperatively
// from a click handler, not on mount.
export function useCustomTaskAssistant() {
  const request = useRequest();
  return useMutation({
    mutationFn: (agencyId) =>
      request(GET, "/assistant/custom-task-agent", {
        query: `agency_id=${agencyId}&assistant_type=Custom Task`,
      }),
  });
}

export function useCreateAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant", { payload, toaster: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistants"] });
      queryClient.invalidateQueries({ queryKey: ["prompt-labels"] });
    },
  });
}

export function useCreateTemplate() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant_template", { payload, toaster: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistants"] });
      queryClient.invalidateQueries({ queryKey: ["prompt-labels"] });
    },
  });
}

export function useUpdateTemplate() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/assistant_template", { payload, toaster: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistants"] });
      queryClient.invalidateQueries({ queryKey: ["prompt-labels"] });
    },
  });
}

// promptlabels/ModelPrompts/Create.js calls this and never reads the result -
// the old AppService code did the same (queryFn's return value was never
// destructured into `data:`). Looks like a deliberate cache warm-up for
// whoever else reads ["model-options"]; preserved as-is rather than "fixed".
export function useModelOptions(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["model-options"],
    queryFn: async () => {
      const { response } = await request(GET, "/modalcreate");
      return response?.data || [];
    },
    ...options,
  });
}

export function useSuperAdminSettings(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["super-admin-settings"],
    queryFn: async () => {
      const { response, error } = await request(GET, "/super_admin_settings/");
      if (error) {
        console.error("Error fetching data:", error);
        return undefined;
      }
      return response?.data;
    },
    ...options,
  });
}
